import React, { useRef, useState, useEffect } from "react";
import { client, urlFor } from "../sanity";
import { useLocation, useNavigate } from "react-router-dom";
import "../Styles/About.css";
import { FaMapMarkerAlt, FaPhoneAlt, FaClock, FaStar, FaPlus, FaMinus, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import SeventhSection from "./SeventhSection";
import comfortimage from "../Assets/comfort-safety-images.jpg";
import openDoor from "../Assets/open-door-image.jpg";

const AboutPage = () => {
  const features = [
    { id: 1, title: "No Delays", desc: "We ensure punctuality for every journey, so you arrive relaxed and on time." },
    { id: 2, title: "Premium Support", desc: "Our dedicated team is available 24/7 to cater to your every need." },
    { id: 3, title: "High Quality", desc: "Experience world-class vehicles maintained to the highest standards." },
    { id: 4, title: "A Diverse Selection", desc: "Choose from a fleet of luxurious cars tailored for every occasion." },
  ];

  const sliderRef = useRef(null);
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const carsPerSlide = 3;
  const [scrollIndex, setScrollIndex] = useState(0);
  const totalSlides = Math.ceil(cars.length / carsPerSlide);
  const location = useLocation();
  const faqRef = useRef(null);

  const scrollByCard = (direction) => {
    const slider = sliderRef.current;
    if (!slider) return;

    const slideWidth = slider.offsetWidth;
    const newScroll = direction === "next"
      ? slider.scrollLeft + slideWidth
      : slider.scrollLeft - slideWidth;

    slider.scrollTo({ left: newScroll, behavior: "smooth" });
    const index = Math.round(newScroll / slideWidth);
    setScrollIndex(Math.min(Math.max(index, 0), totalSlides - 1));
  };


  useEffect(() => {
    if (location.state?.highlight === "faq" && faqRef.current) {
      faqRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      faqRef.current.classList.add("highlight-section");
      setTimeout(() => faqRef.current.classList.remove("highlight-section"), 2000);
    }
  }, [location.state]);


  useEffect(() => {
    const fetchCars = async () => {
      try {
        const query = `*[_type == "vehicle"]{
          _id, name, description, price, rating, image
        }`;
        const data = await client.fetch(query);
        setCars(data);
      } catch (err) {
        console.error("Error fetching vehicles:", err);
      }
    };
    fetchCars();
  }, []);

  const faqData = [
    { question: "Do You Rent To International Visitors?", answer: "Absolutely, we welcome travelers from around the world." },
    { question: "Do You Have Insurance?", answer: "All our vehicles are fully insured for your peace of mind." },
    { question: "Can I Choose My Chauffeur?", answer: "Yes, you may select a preferred chauffeur for your journey." },
    { question: "Are Your Drivers Certified?", answer: "All our drivers are professional and certified for your safety." },
    { question: "What Age Is Required To Rent?", answer: "Drivers must be at least 21 years old with a valid license." }
  ];

  const [activeIndex, setActiveIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const toggleFAQ = (index) => setActiveIndex(activeIndex === index ? null : index);

  return (
    <div className="about-page">
      {/* HERO SECTION */}
      <section className="about-hero"></section>

      {/* FEATURES */}
      <section className="features-section-about">
        <div className="features-container-about">
          <h2 className="compulsory">Exceptional Service, <br/> Designed For Your Comfort</h2>
          <p className="gold-text">Luxury Transportation</p>
          <div className="separator"></div>
          <div className="features-grid">
            {features.map(f => (
              <div key={f.id} className="feature-item-about">
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEVENTH SECTION */}
      <SeventhSection />

      {/* FLEET SLIDER */}
      <section className="fleet-slider-section">
        <div className="fleet-header">
          <h2>Ride In Style With <br/> Maximum Satisfaction</h2>
          <p className="fleet-subheading">Experience true luxury</p>
        </div>
        <div className="fleet-slider-wrapper">
          <div className="fleet-slider" ref={sliderRef}>
            {cars.map(car => (
              <div key={car._id} className="fleet-card">
                <div className="fleet-card-content">
                  <img src={urlFor(car.image)} alt={car.name} />
                  <h3>{car.name}</h3>
                  <div className="fleet-rating">
                    {[...Array(car.rating || 5)].map((_, i) => <FaStar key={i} className="star" />)}
                  </div>
                  <p className="fleet-description">{car.description}</p>
                  <p className="fleet-price"></p>
                  {/* {car.price} */}
                  <button className="fleet-btn" onClick={() => navigate(`/options/${car._id}`)}>
                    <FaPlus className="btn-icon" /> More Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="fleet-slider-counter">
          <FaChevronLeft
            className={`counter-arrow ${scrollIndex === 0 ? "disabled" : ""}`}
            onClick={() => scrollByCard("prev")}
          />
          <span>{scrollIndex + 1} / {totalSlides}</span>
          <FaChevronRight
            className={`counter-arrow ${scrollIndex >= totalSlides - 1 ? "disabled" : ""}`}
            onClick={() => scrollByCard("next")}
          />
        </div>
      </section>

      {/* COMFORT & SAFETY */}
      <section className="comfort-safety-section">
        <div className="comfort-safety-container">
          <div className="comfort-image">
            <img src={comfortimage} alt="Comfort and Safety" />
          </div>
          <div className="comfort-text">
            <h2>Safety And Comfort <br/> In Every Journey</h2>
            <h3>Professional Drivers</h3>
            <p>Our chauffeurs prioritize your safety and deliver an unparalleled travel experience.</p>
            {/* <button className="comfort-btn"><FaPlus className="btn-icon" /> Learn More</button> */}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section" >
        <h2 ref={faqRef}>Frequently Asked Questions</h2>
        <div className="faq-accordion">
          {faqData.map((item, index) => (
            <div key={index} className="faq-item">
              <button
                className={`faq-question ${activeIndex === index ? "active" : ""}`}
                onClick={() => toggleFAQ(index)}
                onMouseEnter={() => setHoverIndex(index)}
                onMouseLeave={() => setHoverIndex(null)}
              >
                {item.question}
                <span className={`faq-icon ${hoverIndex === index ? "hover" : ""}`}>
                  {activeIndex === index ? <FaMinus /> : <FaPlus />}
                </span>
              </button>
              <div
                className="faq-answer"
                style={{ maxHeight: activeIndex === index ? "500px" : "0", opacity: activeIndex === index ? 1 : 0 }}
              >
                <p>{item.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* EXPERIENCE */}
      <section className="experience-section">
        <div className="experience-text">
          <h2>Tailored For Your Needs</h2>
          <h3>Only The Finest</h3>
          <p>Delivering exceptional travel experiences with attention to every detail.</p>
          <div className="experience-stats">
            <div className="stat-item">
              <h4>21<span>+</span></h4>
              <p>Years of Excellence</p>
            </div>
            <div className="stat-item">
              <h4>157<span>k</span></h4>
              <p>Happy Clients</p>
            </div>
          </div>
        </div>
        <div className="experience-image">
          <img src={openDoor} alt="Luxury Experience" />
        </div>
      </section>

      {/* CONTACT */}
      <section className="promo-contact-section">
        <div className="contact-section">
          <div className="contact-column">
            <FaMapMarkerAlt className="contact-icon-big" />
            <p>41 Gade Street Gwarinpa Abuja, FCT.</p>
          </div>
          <div className="contact-column">
            <FaPhoneAlt className="contact-icon-big" />
            <div>
              <p>Phone: (234) 703 0792 420</p>
              <p>Email: oyeanthony@gmail.com</p>
            </div>
          </div>
          <div className="contact-column">
            <FaClock className="contact-icon-big" />
            <div>
              <p>Mon - Sat: 09:00 - 23:00</p>
              <p>Sunday: Closed</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
